import { ExpandMore } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { Accordion, AccordionDetails, Button, Chip, Grid, Stack, TextField, Typography } from '@mui/material';
import AccordionSummary from '@mui/material/AccordionSummary';
import { GridColDef } from '@mui/x-data-grid-pro';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { UseLazyQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { t } from 'i18next';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { OrganisationBadge } from '../../components/Badges/OrganisationBadge';
import { UserBadge } from '../../components/Badges/UserBadge';
import DatagridWrapper, { DataGridId } from '../../components/DatagridWrapper';
import InfoButton from '../../components/InfoButton';
import { OrganisationContext } from '../../components/Layout/OrganisationPicker';
import { DateToFormattedString } from '../../components/Logic/DateLogic';
import ToggleableAutocomplete from '../../components/ToggleableAutocomplete';
import ToggleableDesktopDatePicker from '../../components/ToggleableDesktopDatePicker';
import { AuditEventQueryPackage } from '../../models/API/QueryParams/AuditEventQueryPackage';
import { PaginationQueryPackage } from '../../models/API/QueryParams/PaginationQueryPackage';
import Event, { OperationEnum } from '../../models/Event';
import { OrganisationResponse, SimpleBaseOrganisation } from '../../models/Organisation';
import { UserResponse } from '../../models/User';
import { GetManyPackage } from '../../redux/GetManyPackage';
import { useLazyGetAuthorizationsForOrganisationQuery } from '../AuthorisationApi/redux/authroisationApiSlice';
import { useGetAllOrganisationsQuery } from '../OrganisationApi/redux/organisationApiSlice';
import { NotCorrectRights } from '../Permission/NotCorrectRights';
import { PermissionEnum } from '../Permission/PermissionEnum';
import { mapAuthorisationToUser } from '../ServiceApi/Logic/mappers';
import { AbilityContext, Can } from '../UserApi/logic/Can';
import { useLazyGetAllUsersByOrganisationQuery } from '../UserApi/redux/userApiSlice';
import InfoDrawer from '../../components/InfoDrawer';
import { EventInfoDialog } from './EventInfoDialog';

class EventsListProps {
    getEvents!: UseLazyQuery<QueryDefinition<GetManyPackage<object>, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>, any, Event[], any>>
    idToFetch?: string
}

export const EventsList = (props: EventsListProps) => {
    const ability = useContext(AbilityContext);
    if (!ability.can(PermissionEnum.AUDIT_READ))
        return <NotCorrectRights />

    return (<EventsListContent {...props} />)
}

const EventsListContent = (props: EventsListProps) => {

    const ability = useContext(AbilityContext);
    const organisationContext = useContext(OrganisationContext);
    const query = useQuery();

    const getEventLazy = props.getEvents();
    const getEventLazyTrigger = getEventLazy[0];

    const [clickedRow, setClickedRow] = useState<Event | undefined>(undefined)
    const [events, setEvents] = useState<Event[] | undefined>(undefined)
    const [queryPackage, setQueryPackage] = useState(getDefaultSearch(organisationContext?.organisation));
    const [users, setUsers] = useState<UserResponse[] | undefined>(undefined);

    const getUsersByOrganisation = useLazyGetAllUsersByOrganisationQuery()[0];
    const getAuthorisationsByOrganisation = useLazyGetAuthorizationsForOrganisationQuery()[0];

    const allOrganisationsResult = useGetAllOrganisationsQuery({
        pagination: new PaginationQueryPackage()
    });

    const getColumns = (events: Event[]) => {

        const columns: GridColDef[] = [
            //Data
            { field: 'resource', sortable: true, type: "string", headerName: t('Resource'), flex: 2 },
            { field: 'operation', sortable: true, type: "singleSelect", valueOptions: Object.values(OperationEnum).map(e => t(e) + ""), headerName: t('Operation'), valueGetter: (operation) => t(operation + ""), flex: 2 },
            { field: 'audit_event_date_time', sortable: true, type: "dateTime", headerName: t('Time'), valueGetter: (time) => time && new Date(time), renderCell: (params: { row: Event }) => DateToFormattedString(params.row.audit_event_date_time), flex: 2 },
            { field: 'user', sortable: true, headerName: t('User'), type: "string", valueGetter: (_, row: Event) => row.system_user ? "system" : row.user?.email, renderCell: (params: { row: Event }) => params.row.system_user ? <UserBadge textOverride={t("system")} /> : <UserBadge textOverride={params.row.user?.email} />, flex: 2 },
            { field: 'request_id', sortable: true, headerName: t('Request ID'), type: "string", valueGetter: (_, row: Event) => row.audit_data?.request_info?.request_id, flex: 2 },
            //Buttons
            {
                field: 'events', sortable: false, hideable: false, type: "string", valueGetter: () => "", headerName: t('Actions'), minWidth: 150, renderCell: (params => {
                    const event = events.find(x => x.uuid === params.id)!;

                    return (
                        <Can I={PermissionEnum.AUDIT_READ} this={event}>
                            <InfoButton onClick={() => setClickedRow(event)} />
                        </Can>
                    )
                })
            }
        ];
        return columns
    }

    function updateQueryPackage(queryPackage: AuditEventQueryPackage): void {
        setQueryPackage(queryPackage)
        setClickedRow(undefined)
        setEvents(undefined)
    }

    function resetQueryPackage(): void {
        updateQueryPackage(getDefaultSearch(organisationContext?.organisation))
    }

    useMemo(() => {
        const entityUuid = query.get("entity_uuid");
        if (entityUuid != null) {
            const queryPackageClone = Object.create(queryPackage) as AuditEventQueryPackage
            queryPackageClone.entity_uuid = entityUuid
            updateQueryPackage(queryPackageClone)
        }
    }, [])

    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (ability.can(PermissionEnum.AUDIT_READ)) {
                getEventLazyTrigger({
                    uuid: props.idToFetch,
                    pagination: queryPackage
                }).unwrap().then((result) => {
                    setEvents(result);
                })
            }
        }, 500)

        return () => clearTimeout(delayedSearch)
    }, [queryPackage, props.idToFetch])

    useEffect(() => {
        const queryPackageClone = Object.create(queryPackage) as AuditEventQueryPackage
        queryPackageClone.organisation = organisationContext?.organisation
        updateQueryPackage(queryPackageClone)
    }, [organisationContext?.organisation])

    useEffect(() => {
        const queryPackageClone = Object.create(queryPackage) as AuditEventQueryPackage
        queryPackageClone.audit_user = undefined
        updateQueryPackage(queryPackageClone)

        if (!queryPackage.organisation) {
            setUsers([])
        } else {
            const usersPromise = !ability.can(PermissionEnum.USER_READ, queryPackage.organisation) ? Promise.resolve([]) :
                getUsersByOrganisation({
                    pagination: new PaginationQueryPackage(),
                    uuid: queryPackage.organisation?.uuid
                }).unwrap()
            const authorisationsPromise = !ability.can(PermissionEnum.AUTHORISATION_READ, queryPackage.organisation) ? Promise.resolve([]) :
                getAuthorisationsByOrganisation({
                    pagination: new PaginationQueryPackage(),
                    uuid: queryPackage.organisation?.uuid
                }).unwrap()
            Promise.all([usersPromise, authorisationsPromise])
                .then(([users, authorisations]) => {
                    setUsers([...users, ...authorisations.map(authorisation => mapAuthorisationToUser(authorisation))])
                })
        }

    }, [queryPackage.organisation])

    return (
        <>
            <Stack direction="row" spacing={2} paddingBottom={2}>
                <ToggleableDesktopDatePicker
                    label={t("Start date") + ""}
                    shouldDisableTime={() => false}
                    // key={props.fieldName + value}
                    ampm={false}
                    ampmInClock={false}
                    value={queryPackage.from == undefined ? null : queryPackage.from}
                    onChange={(newDate) => {
                        const query = Object.create(queryPackage) as AuditEventQueryPackage
                        query.from = newDate == null ? undefined : newDate;
                        updateQueryPackage(query)
                    }}
                    inputFormat={"dd-MM-yyyy HH:mm"}
                    renderInput={(params) => <TextField{...params} />}
                    editMode={true} />
                <ToggleableDesktopDatePicker
                    label={t("End date") + ""}
                    shouldDisableTime={() => false}
                    // key={props.fieldName + value}
                    ampm={false}
                    ampmInClock={false}
                    value={queryPackage.to == undefined ? null : queryPackage.to}
                    onChange={(newDate) => {
                        const query = Object.create(queryPackage) as AuditEventQueryPackage
                        query.to = newDate == null ? undefined : newDate;
                        updateQueryPackage(query)
                    }}
                    inputFormat={"dd-MM-yyyy HH:mm"}
                    renderInput={(params) => <TextField{...params} />}
                    editMode={true} />
            </Stack>

            <TextField
                fullWidth
                label={t("Search") + ""}
                value={queryPackage.search_query}
                onChange={(e) => {
                    const query = Object.create(queryPackage) as AuditEventQueryPackage
                    query.search_query = e.target.value
                    updateQueryPackage(query)
                }}
                InputProps={{ startAdornment: <SearchIcon sx={{ marginRight: 1 }} /> }}
            />

            <Accordion
                sx={{ marginBottom: 2 }}
                elevation={0}
                disableGutters>
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    sx={{
                        flexDirection: "row-reverse",
                        '& .MuiAccordionSummary-content': {
                            marginLeft: 2,
                        },
                        '& .MuiAccordionSummary-content.Mui-expanded': {
                            marginLeft: 2,
                        },
                    }}
                    expandIcon={<ExpandMore />}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs="auto">
                            <Typography>{t("Advanced search") + ""}</Typography>
                        </Grid>
                        <Grid item xs>
                            <SearchParamater title='Page' value={queryPackage.page == undefined ? undefined : queryPackage.page + ""} />
                            <SearchParamater title='Pagesize' value={queryPackage.pagesize == undefined ? undefined : queryPackage.pagesize + ""} />
                            <SearchParamater title='Operation' value={queryPackage.operation} />
                            <SearchParamater title='Order by' value={queryPackage.order_by} />
                            <SearchParamater title='Sort by' value={queryPackage.sort_by} />
                            <SearchParamater title='Resource' value={queryPackage.resource} />
                            <SearchParamater title='User organisation' value={queryPackage.organisation?.name} />
                            <SearchParamater title='User' value={queryPackage.audit_user?.email} />
                            <SearchParamater title='Entity' value={queryPackage.entity_uuid} />
                            <Button
                                variant='outlined'
                                onClick={(e) => { resetQueryPackage(); e.stopPropagation() }}>
                                {t("Reset search") + ""}
                            </Button>
                        </Grid>

                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container>
                        <Grid item xs={6}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <QueryFilter<OperationEnum>
                                        value={queryPackage.operation}
                                        label={t("Operation")}
                                        options={Object.values(OperationEnum)}
                                        onChange={(value) => {
                                            const query = Object.create(queryPackage) as AuditEventQueryPackage
                                            query.operation = value;
                                            updateQueryPackage(query)
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <QueryFilter<string>
                                        value={queryPackage.resource}
                                        label={t("Resource")}
                                        options={[...new Set(getEventLazy[1].data?.map(x => x.resource ?? "").filter(x => x != "") ?? [])]}
                                        onChange={(value) => {
                                            const query = Object.create(queryPackage) as AuditEventQueryPackage
                                            query.resource = value;
                                            updateQueryPackage(query)
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <QueryFilter<"ORGANISATION" | "OPERATION" | "EVENT_TIME" | "USER" | "ENTITY" | "RESOURCE">
                                        value={queryPackage.sort_by}
                                        label={t("Sort by")}
                                        options={["ORGANISATION", "OPERATION", "EVENT_TIME", "USER", "ENTITY", "RESOURCE"]}
                                        onChange={(value) => {
                                            const query = Object.create(queryPackage) as AuditEventQueryPackage
                                            query.sort_by = value;
                                            updateQueryPackage(query)
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <QueryFilter<"ASC" | "DESC">
                                        value={queryPackage.order_by}
                                        label={t("Order by")}
                                        options={["ASC", "DESC"]}
                                        onChange={(value) => {
                                            const query = Object.create(queryPackage) as AuditEventQueryPackage
                                            query.order_by = value;
                                            updateQueryPackage(query)
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <QueryFilter<SimpleBaseOrganisation>
                                        value={queryPackage.organisation}
                                        label={t("User organisation")}
                                        options={allOrganisationsResult.data ?? []}
                                        getOptionLabel={(value) => value.name ?? ""}
                                        onChange={(value) => {
                                            const query = Object.create(queryPackage) as AuditEventQueryPackage
                                            query.organisation = value;
                                            updateQueryPackage(query)
                                        }}
                                        renderOption={(value) => <OrganisationBadge ressource={value} noLink />}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <QueryFilter<UserResponse>
                                        value={queryPackage.audit_user}
                                        label={t("User")}
                                        options={users ?? []}
                                        getOptionLabel={(value) => value.email ?? ""}
                                        onChange={(value) => {
                                            const query = Object.create(queryPackage) as AuditEventQueryPackage
                                            query.audit_user = value;
                                            updateQueryPackage(query)
                                        }}
                                        renderOption={(value) => <UserBadge ressource={value} noLink />}
                                        noOptionsText={queryPackage.organisation ? undefined : t("Select a user organisation")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
            <DatagridWrapper
                dataGridId={DataGridId.EVENTS}
                onRowClick={(row) => { setClickedRow(row.row) }}
                disableColumnFilter
                loading={getEventLazy[1].isLoading || !users || !events}
                rows={events ?? []}
                columns={getColumns(events ?? [])}
                initialColumnVisibilityModel={{
                    request_id: false,
                }}
            />

            <InfoDrawer
                open={clickedRow != undefined}
                onClose={() => setClickedRow(undefined)}
                entityUuid={clickedRow?.uuid}>
                {clickedRow &&
                    <EventInfoDialog
                        key={clickedRow.uuid}
                        event={clickedRow} />}
            </InfoDrawer>
        </>
    )
}

export function QueryFilter<T>(props: { loading?: boolean, label: string, value?: T, options: T[], onChange: (value: T) => void, getOptionLabel?: (option: T) => string | JSX.Element, renderOption?: (option: T) => JSX.Element, noOptionsText?: string }) {
    let optionlabel = props.getOptionLabel;
    if (optionlabel == undefined)
        optionlabel = (option) => option + "";

    return (
        <ToggleableAutocomplete
            key={props.value + ""}
            options={props.options}
            noOptionsText={props.noOptionsText}
            editMode={true}
            value={props.value}
            onChange={(_e, value) => props.onChange(value)}
            getOptionLabel={optionlabel}
            renderInput={(params) => (
                <TextField label={props.label}{...params} />)}
            renderOption={props.renderOption ? ((_, option, __) => props.renderOption?.(option)) : undefined}
            isOptionEqualToValue={(option, value) => option == value}
            multiple={false} />
    )
}

function getDefaultSearch(contextOrganisation?: OrganisationResponse) {
    const lastWeek = new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000);
    return new AuditEventQueryPackage({
        from: lastWeek,
        to: new Date(),
        sort_by: "EVENT_TIME",
        order_by: "DESC",
        organisation: contextOrganisation,
        search_query: ""
    })
}

function SearchParamater(props: { title: string, value: string | undefined }) {

    if (props.value == undefined)
        return <></>

    return (
        <Chip key={props.title + props.value} sx={{ padding: "1em", marginRight: "1em" }} label={
            <Stack alignItems="center" direction="row" spacing={2}>
                <Typography variant='caption'>{t(props.title) + ""}</Typography>
                <Typography>{t(props.value) + ""}</Typography>
            </Stack>
        } />

    )
}


function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}